/**
 * Re-autentisering for systemadminen (FF-11, #179): passord og en ny kode fra
 * autentiseringsappen gir 10 minutter der panelet kan endre familier.
 *
 * `useReauthGate().guard(action)` kjører handlingen; svarer serveren 403
 * `reauth_required`, åpnes dialogen, og handlingen kjøres på nytt når
 * re-autentiseringen lykkes. Handlingen viser sine egne feil — bare
 * `reauth_required` skal kastes videre fra den.
 */
import { createPortal } from 'react-dom';
import { useCallback, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { asApiError } from '../../lib/api';
import { PasswordField } from '../auth/AuthFields';
import { SystemApi } from './systemApi';

export function isReauthRequired(err: unknown): boolean {
  const e = asApiError(err);
  return e.status === 403 && e.code === 'reauth_required';
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function SystemReauthDialog({ open, onClose, onSuccess }: DialogProps): ReactElement | null {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!open) return null;

  async function submit(): Promise<void> {
    if (pending || !password || !code) return;
    setPending(true);
    setError(null);
    try {
      await SystemApi.reauth({ password, code: code.trim() });
      setPassword('');
      setCode('');
      onSuccess();
    } catch (e) {
      const err = asApiError(e);
      if (err.code === 'code_already_used') setError(t('system.reauth.code_used'));
      else if (err.code === 'locked') setError(t('system.reauth.locked', { seconds: err.retryAfter ?? 60 }));
      else if (err.status === 0) setError(t('error.offline'));
      else setError(t('system.reauth.wrong'));
      setCode('');
    } finally {
      setPending(false);
    }
  }

  function close(): void {
    setPassword('');
    setCode('');
    setError(null);
    onClose();
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="system-reauth-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
      data-testid="system-reauth-dialog"
    >
      <form
        className="flex w-full max-w-sm flex-col gap-4 rounded-lg bg-sheet p-6 shadow-lg"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h2 id="system-reauth-title" className="m-0 text-heading-s font-extrabold text-ink">
          {t('system.reauth.title')}
        </h2>
        <p className="m-0 text-sm text-ink-2">{t('system.reauth.body')}</p>
        <PasswordField
          label={t('auth.password')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          testId="system-reauth-password"
        />
        <div className="flex flex-col gap-1.5">
          <SectionLabel as="label" htmlFor="system-reauth-code" flush>
            {t('system.login.code')}
          </SectionLabel>
          <input
            id="system-reauth-code"
            data-testid="system-reauth-code"
            className="field tnum"
            type="text"
            inputMode="text"
            autoComplete="one-time-code"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={64}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        {error ? (
          <p data-testid="system-reauth-error" role="alert" className="m-0 text-sm text-bad">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={close} data-testid="system-reauth-cancel">
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pending || !password || !code} data-testid="system-reauth-submit">
            {t('system.reauth.submit')}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

/** Kjører en handling bak re-autentisering. Se fila over. */
export function useReauthGate(): { guard: (action: () => Promise<void>) => Promise<void>; dialog: ReactElement } {
  const [open, setOpen] = useState(false);
  const pendingAction = useRef<(() => Promise<void>) | null>(null);

  const guard = useCallback(async (action: () => Promise<void>): Promise<void> => {
    try {
      await action();
    } catch (e) {
      if (!isReauthRequired(e)) throw e;
      pendingAction.current = action;
      setOpen(true);
    }
  }, []);

  const dialog = (
    <SystemReauthDialog
      open={open}
      onClose={() => {
        pendingAction.current = null;
        setOpen(false);
      }}
      onSuccess={() => {
        const action = pendingAction.current;
        pendingAction.current = null;
        setOpen(false);
        if (action) void guard(action);
      }}
    />
  );
  return { guard, dialog };
}
